import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../components/FirebaseConfig';
import { API_BASE } from '../apiSession';

const ProfileDataContext = createContext();

export const useProfileData = () => {
  const context = useContext(ProfileDataContext);
  if (!context) {
    throw new Error('useProfileData must be used within a ProfileDataProvider');
  }
  return context;
};

export const ProfileDataProvider = ({ children }) => {
  const [profileData, setProfileData] = useState({});
  const [usageStats, setUsageStats] = useState({ date: '', counts: {} });
  const [loading, setLoading] = useState(true);

  const fetchProfileData = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const idToken = await user.getIdToken();
      const [profileResponse, entriesResponse] = await Promise.all([
        fetch(`${API_BASE}/api/profile`, { headers: { 'Authorization': `Bearer ${idToken}` } }),
        fetch(`${API_BASE}/api/profile/entries`, { headers: { 'Authorization': `Bearer ${idToken}` } })
      ]);
      
      if (profileResponse.ok) {
        const resJson = await profileResponse.ok ? await profileResponse.json() : { data: {} };
        const rawData = resJson.data || {};
        
        let entries = [];
        if (entriesResponse.ok) {
          const entriesJson = await entriesResponse.json();
          entries = entriesJson.data || [];
        }

        // Design settings come from the API, which scopes them to the resume
        // currently being edited. They used to be read straight from a
        // per-user Firestore document, so switching templates on one resume
        // silently reformatted every other resume the user owned.
        const sectionSettings = rawData.sectionSettings || {};
        
        let sectionOrder = sectionSettings.sectionOrder ? [...sectionSettings.sectionOrder] : ['profile'];

        // Auto-append any section that has entries in Postgres but isn't in the Firestore order yet
        const typeMapping = {
          'EXPERIENCE': 'experience',
          'EDUCATION': 'education',
          'SKILLS': 'skills',
          'PROJECTS': 'projects',
          'CERTIFICATIONS': 'certifications',
          'SUMMARIES': 'summaries'
        };

        entries.forEach(entry => {
          const uiKey = typeMapping[entry.type] || entry.type.toLowerCase();
          if (!sectionOrder.includes(uiKey)) {
            sectionOrder.push(uiKey);
          }
        });

        const newProfileData = {
          entries,
          sectionSettings,
          sectionOrder,
          usage: rawData.usage || { date: '', counts: {} }
        };

        Object.keys(rawData).forEach(key => {
          const data = rawData[key];
          
          const uiKeyMap = {
            'initialData': 'profile',
            'jobExperience': 'experience'
          };
          const uiKey = uiKeyMap[key] || key;

          if (['sectionSettings', 'usage', 'storyNotes'].includes(key)) {
            if (key === 'usage') setUsageStats(data);
          } else if (data.list !== undefined) {
            newProfileData[uiKey] = data.list;
          } else if (data.data !== undefined) {
            newProfileData[uiKey] = data.data;
          } else if (data.details !== undefined) {
            newProfileData[uiKey] = data.details;
          } else {
            newProfileData[uiKey] = data;
          }
        });

        // Ensure basic keys exist to prevent crashes
        ['profile', 'summaries', 'skills', 'projects', 'education', 'experience', 'certifications'].forEach(k => {
          if (!newProfileData[k]) newProfileData[k] = {};
        });

        setProfileData(newProfileData);
      }
    } catch (err) {
      console.error("Error fetching profile data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        setLoading(true);
        console.log("[Data Protocol] Fetching Profile Data for User:", user.uid);
        fetchProfileData();
      } else {
        // Reset state on Logout
        setProfileData({});
        setUsageStats({ date: '', counts: {} });
        setLoading(false);
      }
    });

    const handleRefresh = () => {
      if (auth.currentUser) fetchProfileData();
    };
    window.addEventListener('refresh-profile', handleRefresh);

    return () => {
      unsubscribeAuth();
      window.removeEventListener('refresh-profile', handleRefresh);
    };
  }, []);

  const refreshUsage = async () => {
    // No longer needed for data, but can be kept as a no-op or force re-fetch if desired.
    // Real-time listener already handles 'usage' doc updates.
  };

  const refreshProfile = async () => {
    await fetchProfileData();
  };

  // Update a single entry locally to avoid a full refresh after minor edits.
  const updateEntryLocal = (updated) => {
    if (!updated || !updated.id) return;
    setProfileData(prev => {
      if (!prev) return prev;
      const entries = Array.isArray(prev.entries) ? prev.entries : [];
      const nextEntries = entries.map(e => e.id === updated.id ? { ...e, data: updated.data, type: updated.type ?? e.type, order: updated.order ?? e.order } : e);
      return { ...prev, entries: nextEntries };
    });
  };

  // Add a single new entry locally (used after creating an entry) so the
  // UI and context stay in sync without a full profile reload.
  const addEntryLocal = (entry) => {
    if (!entry || !entry.id) return;
    setProfileData(prev => {
      if (!prev) return prev;
      const entries = Array.isArray(prev.entries) ? prev.entries : [];
      // Avoid duplicates if it already exists
      if (entries.some(e => e.id === entry.id)) return prev;
      return { ...prev, entries: [...entries, entry] };
    });
  };

  // Remove an entry locally after successful delete so the UI updates
  // immediately without requiring a full profile fetch.
  const removeEntryLocal = (entryId) => {
    if (!entryId) return;
    setProfileData(prev => {
      if (!prev) return prev;
      const entries = Array.isArray(prev.entries) ? prev.entries : [];
      const nextEntries = entries.filter(e => e.id !== entryId);
      return { ...prev, entries: nextEntries };
    });
  };

  return (
    <ProfileDataContext.Provider value={{ profileData, usageStats, refreshUsage, refreshProfile, loading, updateEntryLocal, addEntryLocal, removeEntryLocal }}>
      {children}
    </ProfileDataContext.Provider>
  );
};
